// src/components/wellfinder/ReviewMap.jsx
//
// The adjudication map. A PANEL INSIDE A WORKFLOW, not an exploration surface,
// which is why it is a new component rather than a configuration of MapView.
//
// MapView owns a layer stack, a layer panel, imagery stack state, follow mode,
// compare mode and a time slider — all of which persist across renders because
// exploration is stateful by nature. Every one of those is wrong here: the
// analyst advances to the next record every few seconds and must arrive at an
// identical view each time. Reusing MapView would mean re-navigating a
// preserved viewport after every keystroke.
//
// ── FIXED ZOOM, NOT FIT-BOUNDS ───────────────────────────────────────────────
// The viewport spans ~150m and always contains the 100m candidate search
// radius. Fit-bounds is strictly more informative per record and strictly worse
// in aggregate: a record whose nearest candidate is 5m away would render at a
// wildly different scale than one at 90m, and an analyst moving through
// thousands of records needs their eye to learn ONE scale. A 5m offset has to
// look like 5m every time, not like whatever fills the frame. Manual zoom is
// allowed and resets on record change.
//
// ── LAYER ORDER, BOTTOM TO TOP ───────────────────────────────────────────────
//   imagery raster -> footprint outline -> candidate links -> holder link ->
//   candidates -> holder -> detection
// The detection is topmost and never occluded: it is the thing being
// adjudicated, and a candidate point drawn over it at 4m separation would make
// the two indistinguishable at exactly the distance where the distinction
// matters most (p50 nearest is 4.78m).

import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../../lib/map/whenMapLoaded";
import { getOrFetchTileToken, bindTileAuthHandler } from "../../api/tileTokens";
import { tileUrl } from "../../api/tileUrl";
import { attachAttribution, basemapAttribution, SATELLITE_STYLE_ID } from "../../lib/map/attribution";
import { buildTransformRequest } from "../../api/mapTransformRequest";
import { useAuthStore } from "../../stores/useAuthStore";
import { getConfig } from "../../config";
import MapLegend from "./MapLegend";
import {
  REVIEW_ZOOM,
  DETAIL_ZOOM,
  detectionCirclePaint,
  candidateCirclePaint,
  linkLinePaint,
  footprintLinePaint,
  holderCirclePaint,
  holderLinkPaint,
} from "./reviewMapEncoding";
import { holderFeatures } from "./holderFeatures";

// ── SCALE, CORRECTED ─────────────────────────────────────────────────────────
// An earlier note here said z18 was ~0.45 m/px and a ~340px pane spanned 150m.
// Both were wrong: MapLibre's Transform uses _tileSize = 512, so
// m/px = 40075016.686 * cos(lat) / (512 * 2^zoom) = 0.254 at z18 / 31.7N — half
// what the 256-tile convention gives. Covering the detection plus the full 100m
// candidate radius therefore needs ~790px, not ~400px. See RecordDetail for
// what the pane actually delivers and which records that leaves partly outside
// the frame. The zoom itself is unchanged: legibility at z18 was confirmed by
// eye, and moving it would invalidate that.

const SRC = {
  imagery: "wfr-imagery",
  footprint: "wfr-footprint",
  links: "wfr-links",
  candidates: "wfr-candidates",
  detection: "wfr-detection",
  holder: "wfr-holder",
};

const EMPTY_FC = { type: "FeatureCollection", features: [] };

function candidateFeatures(candidates, activeRank) {
  return {
    type: "FeatureCollection",
    features: (candidates ?? [])
      .filter((c) => c.rrc_lon != null && c.rrc_lat != null)
      .map((c) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [c.rrc_lon, c.rrc_lat] },
        properties: {
          rank: c.rank,
          // Booleans as 0/1: MapLibre feature-state is not in play here and a
          // filter on a real boolean works, but the paint expressions read
          // better against numbers and this matches WellLayer's convention.
          primary: c.rank === 1 ? 1 : 0,
          active: c.rank === activeRank ? 1 : 0,
        },
      })),
  };
}

function linkFeatures(detection, candidates, activeRank) {
  const origin = detection?.coordinates;
  if (!origin) return EMPTY_FC;
  return {
    type: "FeatureCollection",
    features: (candidates ?? [])
      .filter((c) => c.rrc_lon != null && c.rrc_lat != null)
      .map((c) => ({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [origin, [c.rrc_lon, c.rrc_lat]] },
        properties: {
          rank: c.rank,
          primary: c.rank === 1 ? 1 : 0,
          active: c.rank === activeRank ? 1 : 0,
        },
      })),
  };
}

export default function ReviewMap({
  detail,
  activeRank = null,
  footprintVisible = true,
  onHoverCandidate,
  onSelectCandidate,
  onImageryError,
  imageryError = null,
  geomMode = false,
  geomDraft = null,
  boundaryMode = false,
  boundaryDraft = null,
  onBoundaryDraft,
  onGeomDraft,
  zoom = REVIEW_ZOOM,
  holder: holderPoint = null,
}) {
  const holder = useRef(null);
  const mapRef = useRef(null);
  const readyRef = useRef(false);
  // Handlers change every render; the map's click/hover listeners are bound
  // once. A ref keeps the binding stable without re-registering on every
  // keystroke, which would leak listeners at one per record.
  // The record-change effect reads the zoom without depending on it, so a
  // toggle does not re-run the recentre-and-reset block.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const cb = useRef({});
  cb.current = { onHoverCandidate, onSelectCandidate, onGeomDraft, onBoundaryDraft };
  const markerRef = useRef(null);
  const vertexMarkersRef = useRef([]);

  const detection = detail?.geometry?.effective ?? detail?.geometry?.detection ?? null;
  const datasetKey = detail?.imagery?.dataset_key ?? null;

  // ── Map lifecycle ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!holder.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: holder.current,
      style: {
        version: 8,
        sources: {
          "mapbox-satellite": {
            type: "raster",
            tiles: [
              `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${getConfig().mapboxToken}`,
            ],
            tileSize: 256,
            attribution: basemapAttribution(SATELLITE_STYLE_ID),
          },
        },
        layers: [{ id: "mapbox-satellite", type: "raster", source: "mapbox-satellite" }],
      },
      center: [-102, 32],
      zoom: REVIEW_ZOOM,
      attributionControl: { compact: false },
      maxzoom: 20,
      // No NavigationControl and no GeolocateControl. Panning to somewhere else
      // in the Permian is not a thing this panel is for.
      transformRequest: buildTransformRequest(() => useAuthStore.getState().token),
    });

    // Mapbox's logo and credit (REM-684): AI PRIME imagery sits on Mapbox Satellite.
    attachAttribution(map, { mapboxSource: "mapbox-satellite" });
    mapRef.current = map;
    // Matches the __mapA / __mapB convention so the same debugging habits work.
    window.__mapReview = map;
    bindTileAuthHandler(map);

    whenMapLoaded(map, () => {
      readyRef.current = true;

      map.addSource(SRC.footprint, { type: "geojson", data: EMPTY_FC });
      map.addSource(SRC.links, { type: "geojson", data: EMPTY_FC });
      map.addSource(SRC.candidates, { type: "geojson", data: EMPTY_FC });
      map.addSource(SRC.detection, { type: "geojson", data: EMPTY_FC });
      map.addSource(SRC.holder, { type: "geojson", data: EMPTY_FC });

      // Outline only. The footprint is ~62 m² inside a ~150m viewport, so a
      // filled polygon would be a coloured speck sitting on the imagery the
      // analyst is trying to read.
      map.addLayer({
        id: "wfr-footprint-line", type: "line", source: SRC.footprint,
        paint: footprintLinePaint(),
      });

      // The polygon being edited. FILLED, unlike the footprint outline above:
      // while the mode is on the analyst is reading the SHAPE rather than the
      // imagery under it, and a bare outline among four drag handles is hard to
      // read as an enclosed area. Empty except while editing.
      map.addSource(SRC.boundaryDraft, { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "wfr-boundary-draft-fill", type: "fill", source: SRC.boundaryDraft,
        paint: { "fill-color": "#FF7F00", "fill-opacity": 0.12 },
      });
      map.addLayer({
        id: "wfr-boundary-draft-line", type: "line", source: SRC.boundaryDraft,
        paint: { "line-color": "#FF7F00", "line-width": 1.5 },
      });

      map.addLayer({
        id: "wfr-links-line", type: "line", source: SRC.links,
        paint: linkLinePaint(),
      });

      map.addLayer({
        id: "wfr-holder-link", type: "line", source: SRC.holder,
        filter: ["==", ["get", "part"], "link"],
        paint: holderLinkPaint(),
      });

      map.addLayer({
        id: "wfr-candidates-pt", type: "circle", source: SRC.candidates,
        paint: candidateCirclePaint(),
      });

      // Above the candidates, so the well it points at does not hide it, and
      // BELOW our detection, which stays topmost and never occluded.
      map.addLayer({
        id: "wfr-holder-pt", type: "circle", source: SRC.holder,
        filter: ["==", ["get", "part"], "point"],
        paint: holderCirclePaint(),
      });

      // Topmost, and a different SHAPE as well as a different colour. At 4m
      // separation two circles differing only in hue are one blob; a ringed
      // marker stays readable when they overlap.
      map.addLayer({
        id: "wfr-detection-pt", type: "circle", source: SRC.detection,
        paint: detectionCirclePaint(),
      });

      map.on("mousemove", "wfr-candidates-pt", (e) => {
        map.getCanvas().style.cursor = "pointer";
        cb.current.onHoverCandidate?.(e.features?.[0]?.properties?.rank ?? null);
      });
      map.on("mouseleave", "wfr-candidates-pt", () => {
        map.getCanvas().style.cursor = "";
        cb.current.onHoverCandidate?.(null);
      });
      map.on("click", "wfr-candidates-pt", (e) => {
        cb.current.onSelectCandidate?.(e.features?.[0]?.properties?.rank ?? null);
      });
    });

    return () => {
      readyRef.current = false;
      if (window.__mapReview === map) delete window.__mapReview;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ── Imagery ────────────────────────────────────────────────────────────────
  //
  // All 10,803 records resolve to a fine-LOD dataset, so the no-imagery branch
  // is currently unreachable. It is handled and not invested in: the panel
  // falls back to the satellite basemap and says so, rather than rendering
  // blank tiles that look like a loading failure.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !datasetKey) return;
    let cancelled = false;

    const attach = async () => {
      try {
        // The ONLY tile path. ck comes from GET /tile/session via this cache;
        // the token itself never enters the URL — it rides as X-Tile-Token
        // through transformRequest, which is what keeps the URL stable and the
        // map from re-tiling on refresh.
        const { ck } = await getOrFetchTileToken(datasetKey);
        if (cancelled || !mapRef.current) return;
        const url = tileUrl(datasetKey, ck);

        const existing = map.getSource(SRC.imagery);
        if (!existing) {
          map.addSource(SRC.imagery, { type: "raster", tiles: [url], tileSize: 256, maxzoom: 20 });
          // Beneath everything, above the basemap.
          map.addLayer({ id: "wfr-imagery-raster", type: "raster", source: SRC.imagery },
            map.getLayer("wfr-footprint-line") ? "wfr-footprint-line" : undefined);
        } else if (existing.tiles?.[0] !== url) {
          existing.setTiles([url]);
        }
        map.__datasetKeyBySource ||= new Map();
        map.__datasetKeyBySource.set(SRC.imagery, datasetKey);
      } catch (err) {
        if (!cancelled) onImageryError?.(err);
      }
    };

    if (readyRef.current) attach();
    else map.once("load", attach);
    return () => { cancelled = true; };
  }, [datasetKey, onImageryError]);

  // ── Data + recenter, on every record change ────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const apply = () => {
      if (!mapRef.current) return;
      map.getSource(SRC.detection)?.setData(
        detection ? { type: "Feature", geometry: detection, properties: {} } : EMPTY_FC,
      );
      // detail.footprint is a WRAPPER — { id, source_id, geometry, area_m2 } —
      // and the geometry is the `geometry` key inside it. Passing the wrapper
      // straight in as a Feature's geometry produced an object with no `type`,
      // which MapLibre discards silently: no error, no warning, no footprint.
      // That is why the layer rendered nothing while the visibility toggle
      // tested green — the toggle was correctly flipping an empty layer.
      const fpGeom = detail?.footprint?.geometry ?? null;
      map.getSource(SRC.footprint)?.setData(
        // 19 of 10,803 records have no intersecting boundary. An empty
        // collection renders nothing, which is the correct outcome — no gap, no
        // placeholder, no error.
        fpGeom
          ? { type: "Feature", geometry: fpGeom, properties: {} }
          : EMPTY_FC,
      );
      map.getSource(SRC.candidates)?.setData(candidateFeatures(detail?.candidates, activeRank));
      map.getSource(SRC.links)?.setData(linkFeatures(detection, detail?.candidates, activeRank));

      // jumpTo, not flyTo. An animation between two records 40km apart is three
      // seconds of scenery in a workflow measured in seconds per record.
      //
      // ALWAYS REVIEW_ZOOM here, never the detail zoom. The store clears
      // zoomedIn on select(), and this is the other half: no record may inherit
      // the previous one's zoom, or the fixed-scale property that makes 5 m look
      // like 5 m every time is gone.
      // The RECORD'S zoom, not a constant — a record whose rank-1 is within
      // 16m opens at z20 because that is where the equipment judgement is made
      // and the analyst was pressing `z` on every one of them.
      if (detection?.coordinates) {
        map.jumpTo({ center: detection.coordinates, zoom: zoomRef.current });
      }
    };

    if (readyRef.current) apply();
    else map.once("load", apply);
    // detail.record.id rather than detail: the object identity changes on every
    // store write, and recentring on a hover would fight the analyst's zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.record?.id]);

  // Footprint visibility. A layout property rather than emptying the source:
  // toggling it must not disturb the data, because the whole point of the
  // toggle is A/B comparison on the SAME record.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current || !map.getLayer("wfr-footprint-line")) return;
    map.setLayoutProperty(
      "wfr-footprint-line", "visibility", footprintVisible ? "visible" : "none",
    );
  }, [footprintVisible, detail?.record?.id]);

  // ── Detail zoom ────────────────────────────────────────────────────────────
  //
  // Re-centres on the detection as well as zooming: at z20 the frame is 64 m
  // across, so a viewport the analyst had nudged would put the detection off
  // screen. Keyboard only — the complaint was the mouse trip, not the keystroke.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current || !detection?.coordinates) return;
    map.jumpTo({ center: detection.coordinates, zoom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  // ── Point move ─────────────────────────────────────────────────────────────
  //
  // A DRAGGABLE MARKER, NOT MlDraw. The brief called for MlDraw's vertex-edit
  // primitive, and it does not support a Point: _buildHandleFeatures routes
  // vertex mode through _getPolygonCoords and bails on `ring.length > 1`, so a
  // single-coordinate geometry yields no handle and nothing to drag. It also
  // installs six layers and map-wide mousedown/contextmenu handlers, which
  // would contend with this map's own candidate click and hover bindings.
  // maplibregl.Marker({draggable:true}) IS the drag for one point.
  //
  // Explicit mode: the marker only exists while geomMode is on, so there is
  // nothing to catch with a stray drag during a three-second loop. The
  // detection layer stays put underneath as the ORIGINAL position, which is
  // what makes the correction legible — the analyst sees the offset they are
  // introducing, not just the new spot.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!geomMode) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }
    const start = geomDraft ?? detection?.coordinates;
    if (!start) return;

    const el = document.createElement("div");
    el.setAttribute("data-testid", "geom-marker");
    el.style.cssText =
      "width:16px;height:16px;border-radius:50%;background:#FF7F00;" +
      "box-shadow:0 0 0 3px #050711;cursor:grab";
    const m = new maplibregl.Marker({ element: el, draggable: true })
      .setLngLat(start)
      .addTo(map);
    m.on("dragend", () => {
      const { lng, lat } = m.getLngLat();
      cb.current.onGeomDraft?.([lng, lat]);
    });
    markerRef.current = m;
    return () => { m.remove(); markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geomMode, detail?.record?.id]);

  // ── BOUNDARY EDITING: ONE DRAGGABLE MARKER PER CORNER ─────────────────────
  //
  // NOT MlDraw, and for the reason recorded two blocks up plus a measurement.
  // That comment rejected it for geomMode because it installs six layers and
  // map-wide mousedown/contextmenu handlers that contend with this map's own
  // candidate click and hover bindings. Those handlers are no less contentious
  // for a polygon.
  //
  // And the measurement removes the reason to pay that cost: EVERY
  // well_head_boundary IN THE CORPUS IS FIVE POINTS — min 5, mean 5.0, p95 5,
  // max 6, one part. They are rectangles. Four draggable corners is the whole
  // interaction, and maplibregl.Marker({draggable:true}) already does exactly
  // that one block above for the detection point. A general-purpose vertex
  // editor would be more code, more contention and no more capability.
  //
  // The ring closes by construction: the marker array holds the DISTINCT
  // corners and the draft repeats the first as the last, so a dragged corner
  // cannot open the ring.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;

    const clearMarkers = () => {
      for (const m of vertexMarkersRef.current) m.remove();
      vertexMarkersRef.current = [];
    };

    if (!boundaryMode || !boundaryDraft) {
      clearMarkers();
      map.getSource(SRC.boundaryDraft)?.setData(EMPTY_FC);
      return;
    }

    // Outer ring of the first part. Boundaries are single-part MultiPolygons or
    // plain Polygons; both are handled rather than assumed.
    const ring = boundaryDraft.type === "MultiPolygon"
      ? boundaryDraft.coordinates?.[0]?.[0]
      : boundaryDraft.coordinates?.[0];
    if (!Array.isArray(ring) || ring.length < 4) { clearMarkers(); return; }
    const corners = ring.slice(0, -1).map(([lng, lat]) => [lng, lat]);

    const draftFrom = (pts) => {
      const closed = [...pts, pts[0]];
      return boundaryDraft.type === "MultiPolygon"
        ? { type: "MultiPolygon", coordinates: [[closed]] }
        : { type: "Polygon", coordinates: [closed] };
    };
    const paint = (pts) => map.getSource(SRC.boundaryDraft)?.setData({
      type: "Feature", properties: {}, geometry: draftFrom(pts),
    });

    clearMarkers();
    const live = corners.map((c) => [...c]);
    paint(live);

    live.forEach((c, i) => {
      const el = document.createElement("div");
      el.setAttribute("data-testid", `boundary-vertex-${i}`);
      el.style.cssText =
        "width:12px;height:12px;border-radius:2px;background:#FF7F00;" +
        "box-shadow:0 0 0 2px #050711;cursor:grab";
      const m = new maplibregl.Marker({ element: el, draggable: true })
        .setLngLat(c).addTo(map);
      // Repaint on `drag`, not only `dragend`, so the polygon follows the
      // corner. Dragging a handle while the shape stays put reads as the drag
      // having failed.
      m.on("drag", () => { const { lng, lat } = m.getLngLat(); live[i] = [lng, lat]; paint(live); });
      m.on("dragend", () => {
        const { lng, lat } = m.getLngLat();
        live[i] = [lng, lat];
        paint(live);
        cb.current.onBoundaryDraft?.(draftFrom(live));
      });
      vertexMarkersRef.current.push(m);
    });

    return clearMarkers;
    // boundaryDraft is deliberately NOT a dependency: it is what this effect
    // WRITES on every drag, so depending on it would tear down and rebuild the
    // markers mid-gesture. Entering the mode and changing record are the only
    // things that should rebuild them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryMode, detail?.record?.id]);

  // ── THE ZOOM, AND ITS EXIT ────────────────────────────────────────────────
  //
  // A 26.9 m2 boundary at z18 (0.254 m/px at 31.7N) is about 20 px across —
  // roughly a cursor. Vertex editing that is not a thing a person can do.
  //
  // The fixed-scale rule exists so an analyst crossing thousands of records
  // learns one scale, and it is worth protecting at the three-second cadence
  // the queue is built around. Boundary editing is NOT that cadence: it is a
  // deliberate detour, entered on purpose, and a mode entered on purpose can
  // carry its own scale without eroding the rule.
  //
  // Fit rather than a fixed higher zoom: these span 27 m2 to 37 m2+ (the shared
  // ones are half again larger), so a constant is too tight for some and wasted
  // frame for others. Fitting gives every polygon the same PROPORTION of the
  // frame, which is what matters when dragging a corner.
  //
  // EXIT RETURNS TO REVIEW_ZOOM, NOT TO WHATEVER THE FIT PRODUCED. Otherwise
  // the next record inherits a scale — exactly what the rule forbids and what
  // select() clearing zoomedIn already prevents on the other path.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    if (boundaryMode) {
      const ring = boundaryDraft?.type === "MultiPolygon"
        ? boundaryDraft.coordinates?.[0]?.[0]
        : boundaryDraft?.coordinates?.[0];
      if (Array.isArray(ring) && ring.length >= 4) {
        const b = new maplibregl.LngLatBounds(ring[0], ring[0]);
        for (const c of ring) b.extend(c);
        // Generous padding: the corners sit ON the edge of the shape, and a
        // handle flush against the frame cannot be dragged outwards.
        map.fitBounds(b, { padding: 90, duration: 250, maxZoom: 22 });
      }
      return;
    }
    if (detection?.coordinates) {
      map.easeTo({ center: detection.coordinates, zoom: REVIEW_ZOOM, duration: 250 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryMode]);

  // ── THE HOLDER, ON REQUEST ─────────────────────────────────────────────────
  //
  // Same rule as boundary editing two blocks up: the fixed scale protects the
  // three-second loop, and asking to see who holds a well is a deliberate
  // detour out of it. So the camera moves ONLY if the holder or its well is
  // out of frame — measured, the two detections are p50 12-18 m apart, inside
  // the frame, but reach 187 m — and returns to the record's own view when
  // the holder is hidden. It never leaves the next record a scale: select()
  // clears the holder, and the record-change effect above re-centres anyway.
  //
  // maxZoom is the current zoom, so a fit can only widen the view — showing a
  // holder that is already in frame never zooms in on it.
  const fittedRef = useRef(false);
  const holderKey = holderPoint
    ? `${holderPoint.lon},${holderPoint.lat},${holderPoint.wellLon},${holderPoint.wellLat}`
    : null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      if (!mapRef.current) return;
      map.getSource(SRC.holder)?.setData(holderFeatures(holderPoint));
      if (!holderPoint) {
        if (fittedRef.current && detection?.coordinates) {
          map.easeTo({ center: detection.coordinates, zoom: zoomRef.current, duration: 250 });
        }
        fittedRef.current = false;
        return;
      }
      const pts = [[holderPoint.lon, holderPoint.lat]];
      if (holderPoint.wellLon != null) pts.push([holderPoint.wellLon, holderPoint.wellLat]);
      const inFrame = pts.every((p) => map.getBounds().contains(p));
      if (inFrame) return;
      const b = new maplibregl.LngLatBounds(pts[0], pts[0]);
      for (const p of pts) b.extend(p);
      if (detection?.coordinates) b.extend(detection.coordinates);
      map.fitBounds(b, { padding: 60, duration: 250, maxZoom: map.getZoom() });
      fittedRef.current = true;
    };
    if (readyRef.current) apply();
    else map.once("load", apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holderKey, detail?.record?.id]);

  // Highlight only — no recenter. Separated from the block above so hovering a
  // candidate cannot move the viewport.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    map.getSource(SRC.candidates)?.setData(candidateFeatures(detail?.candidates, activeRank));
    map.getSource(SRC.links)?.setData(linkFeatures(detection, detail?.candidates, activeRank));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRank]);

  return (
    <div className="relative h-full w-full">
      <div ref={holder} data-testid="review-map" className="absolute inset-0" />
      <MapLegend showHolder={holderPoint != null} />
      {/* Always says which view you are in, because it is no longer the same
          view every time — a record opens at z20 or z18 depending on how close
          its rank-1 is. */}
      <div className="pointer-events-none absolute right-2 top-2 rounded border border-white/15 bg-bg/90 px-2 py-1 text-[11px]">
        {zoom >= DETAIL_ZOOM ? (
          <span className="text-accent-info">detail · z{DETAIL_ZOOM}</span>
        ) : (
          <span className="text-fg-secondary">context · z{REVIEW_ZOOM}</span>
        )}
        <span className="ml-1 text-fg-secondary/60">z</span>
      </div>
      {geomMode && (
        <div className="pointer-events-none absolute left-2 top-2 rounded border border-accent-action/50 bg-bg/90 px-2 py-1 text-[11px] text-accent-action">
          Move mode — drag the orange point. The blue marker is where the model
          put it. <span className="text-fg-secondary">Esc</span> cancels.
        </div>
      )}
      {imageryError ? (
        // Legible, not blank. A tile failure leaves the satellite basemap
        // underneath, which is visually plausible and at the wrong resolution —
        // an analyst has to be told they are not looking at the imagery they
        // think they are.
        <div
          data-testid="imagery-error"
          className="pointer-events-none absolute inset-x-2 bottom-2 rounded border border-accent-red/50 bg-bg/90 px-2 py-1.5 text-[11px] text-accent-red"
        >
          Imagery unavailable — showing basemap only, which is not the resolution
          this review is calibrated for. Do not adjudicate from it.
        </div>
      ) : !datasetKey ? (
        <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-bg/80 px-2 py-1 text-[11px] text-fg-secondary">
          No imagery dataset covers this location — showing basemap only.
        </div>
      ) : null}
    </div>
  );
}
